import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from "react";
import { registry } from "./registry";

export type Lang = "en" | "fr";

interface I18nContextType {
  lang: Lang;
  setLang: (lang: Lang) => void;
  /** True while the crossfade transition is in progress */
  isTransitioning: boolean;
}

const I18nContext = createContext<I18nContextType>({
  lang: "en",
  setLang: () => {},
  isTransitioning: false,
});

/** Duration (ms) for the fade-out phase before language swap */
const FADE_OUT_MS = 220;

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => {
    if (typeof window === "undefined") return "en";
    const stored = localStorage.getItem("nowquantum-lang");
    if (stored === "fr") return "fr";
    const browserLang = navigator.language.toLowerCase();
    return browserLang.startsWith("fr") ? "fr" : "en";
  });

  const [isTransitioning, setIsTransitioning] = useState(false);
  const pendingLang = useRef<Lang | null>(null);

  useEffect(() => {
    localStorage.setItem("nowquantum-lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);

  /**
   * Orchestrated language switch:
   * 1. Fade out content (FADE_OUT_MS)
   * 2. Swap the language (React re-render)
   * 3. Fade in content (FADE_IN_MS, handled by CSS transition)
   */
  const setLang = useCallback((l: Lang) => {
    if (l === lang || isTransitioning) return;
    pendingLang.current = l;
    setIsTransitioning(true);

    // After fade-out completes, swap the language
    setTimeout(() => {
      setLangState(l);
      // Small delay for React to render new text before fading in
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsTransitioning(false);
          pendingLang.current = null;
        });
      });
    }, FADE_OUT_MS);
  }, [lang, isTransitioning]);

  return (
    <I18nContext.Provider value={{ lang, setLang, isTransitioning }}>
      {children}
    </I18nContext.Provider>
  );
}

/** Hook to access language state and transition info */
export function useLang() {
  return useContext(I18nContext);
}

/** Resolve a dot-notation key against a nested object */
function resolve(obj: Record<string, any>, key: string): string {
  const parts = key.split(".");
  let current: any = obj;
  for (const part of parts) {
    if (current == null || typeof current !== "object") return key;
    current = current[part];
  }
  return typeof current === "string" ? current : key;
}

/** Hook to access translations for a specific page namespace */
export function usePageTranslations(namespace: string) {
  const { lang } = useLang();
  const translations = registry[namespace]?.[lang] ?? {};
  const t = useCallback(
    (key: string) => resolve(translations, key),
    [translations]
  );
  return { t };
}
